// Ações de ciclo da proposta na tela de detalhe (Story 11.7 AC5/AC6).
// Botões condicionais ao status + copiar link público. Toasts e invalidação de
// cache ficam nos hooks/handlers. Mantém o detalhe "burro" sobre transições — a
// validação real é no backend (transição inválida → 400 tratado como toast de erro).

import { useState } from 'react';
import { Check, Copy, Send, ThumbsUp, ThumbsDown, Files, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { CrmProposalStatusSchema } from '@control-center/shared';

import { PillButton } from '../clients/PillButton';
import { RejectProposalDialog } from './RejectProposalDialog';
import { ConvertProposalDialog } from './ConvertProposalDialog';
import {
  useAcceptCrmProposal,
  useConvertCrmProposal,
  useDuplicateCrmProposal,
  useRejectCrmProposal,
  useSendCrmProposal,
} from '../../hooks/use-crm-proposals';
import { extractErrorCode, extractErrorDetails, extractErrorMessage } from '../../lib/errors';

/** Base pública do frontend pra montar o link copiável (mesma origem do app). */
function publicLinkFor(token: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/public/proposals/${token}`;
}

interface ProposalActionsProps {
  id: string;
  proposalNumber: string;
  status: CrmProposalStatusSchema;
  publicToken: string | null;
  convertedSubscriptionId: string | null;
  canWrite: boolean;
  /** Lead vinculado — usado pro link "completar dados" quando faltam campos. */
  leadId?: string | null;
  onEdit?: () => void;
}

export function ProposalActions({
  id,
  proposalNumber,
  status,
  publicToken,
  convertedSubscriptionId,
  canWrite,
  leadId,
  onEdit,
}: ProposalActionsProps): JSX.Element | null {
  const navigate = useNavigate();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);

  const send = useSendCrmProposal();
  const accept = useAcceptCrmProposal();
  const reject = useRejectCrmProposal();
  const duplicate = useDuplicateCrmProposal();
  const convert = useConvertCrmProposal();

  const busy =
    send.isPending ||
    accept.isPending ||
    reject.isPending ||
    duplicate.isPending ||
    convert.isPending;

  // Status terminal `convertida`: só mostra link pra assinatura gerada (read-only).
  if (status === 'convertida') {
    return convertedSubscriptionId ? (
      <ActionBar>
        <PillButton onClick={() => navigate(`/subscriptions/${convertedSubscriptionId}`)}>
          Ver assinatura gerada
        </PillButton>
      </ActionBar>
    ) : null;
  }

  const showCopyLink =
    publicToken !== null &&
    (status === 'enviada' || status === 'visualizada' || status === 'aceita');

  const handleSend = async (): Promise<void> => {
    try {
      await send.mutateAsync(id);
      toast.success('Proposta enviada.', { description: proposalNumber });
    } catch (err) {
      toast.error('Não conseguimos enviar a proposta.', {
        description: extractErrorMessage(err, 'Tente de novo em instantes.'),
      });
    }
  };

  const handleAccept = async (): Promise<void> => {
    try {
      await accept.mutateAsync(id);
      toast.success('Proposta marcada como aceita.', { description: proposalNumber });
    } catch (err) {
      toast.error('Não conseguimos aceitar a proposta.', {
        description: extractErrorMessage(err, 'Verifique o status e tente de novo.'),
      });
    }
  };

  const handleReject = async (reason: string): Promise<void> => {
    try {
      await reject.mutateAsync({ id, rejectionReason: reason });
      setRejectOpen(false);
      toast.success('Proposta recusada.', { description: proposalNumber });
    } catch (err) {
      toast.error('Não conseguimos recusar a proposta.', {
        description: extractErrorMessage(err, 'Verifique o status e tente de novo.'),
      });
    }
  };

  const handleDuplicate = async (): Promise<void> => {
    try {
      const created = await duplicate.mutateAsync(id);
      toast.success('Proposta duplicada.', {
        description: `Novo rascunho ${created.proposal_number}`,
      });
      navigate(`/crm/proposals/${created.id}`);
    } catch (err) {
      toast.error('Não conseguimos duplicar a proposta.', {
        description: extractErrorMessage(err, 'Tente de novo em instantes.'),
      });
    }
  };

  const handleConvert = async (): Promise<void> => {
    try {
      const result = await convert.mutateAsync({ id });
      setConvertOpen(false);
      setMissingFields(null);
      toast.success(
        result.created ? 'Assinatura criada com sucesso!' : 'Proposta já estava convertida.',
        {
          description: proposalNumber,
          action: {
            label: 'Ver assinatura',
            onClick: () => navigate(`/subscriptions/${result.subscriptionId}`),
          },
        },
      );
      navigate(`/subscriptions/${result.subscriptionId}`);
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === 'MISSING_CLIENT_DATA') {
        // Mostra os campos faltantes no próprio modal (não inventa dado — AC3/AC8).
        const details = extractErrorDetails(err);
        const fields = Array.isArray(details?.missingFields)
          ? (details?.missingFields as string[])
          : ['client_or_lead'];
        setMissingFields(fields);
        return;
      }
      if (code === 'PROPOSAL_ALREADY_CONVERTED') {
        // Corrida: alguém converteu primeiro — guia pro detalhe (mostra a assinatura).
        setConvertOpen(false);
        toast.info('Proposta já foi convertida.', {
          description: 'Atualizando para mostrar a assinatura gerada.',
        });
        return;
      }
      toast.error('Não conseguimos converter a proposta.', {
        description: extractErrorMessage(err, 'Nenhum dado foi alterado. Tente de novo.'),
      });
    }
  };

  const openConvert = (): void => {
    setMissingFields(null);
    setConvertOpen(true);
  };

  const handleCopyLink = async (): Promise<void> => {
    if (!publicToken) return;
    try {
      await navigator.clipboard.writeText(publicLinkFor(publicToken));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  // Sem permissão de escrita: ainda permite copiar o link (leitura), nada mais.
  if (!canWrite) {
    return showCopyLink ? (
      <ActionBar>
        <CopyLinkButton copied={copied} onClick={handleCopyLink} />
      </ActionBar>
    ) : null;
  }

  return (
    <>
      <ActionBar>
        {status === 'rascunho' && (
          <>
            {onEdit && <PillButton onClick={onEdit}>Editar</PillButton>}
            <PillButton
              solid
              icon={<Send size={13} strokeWidth={2} />}
              disabled={busy}
              onClick={handleSend}
            >
              {send.isPending ? 'Enviando…' : 'Enviar'}
            </PillButton>
          </>
        )}

        {(status === 'enviada' || status === 'visualizada') && (
          <>
            <PillButton
              solid
              icon={<ThumbsUp size={13} strokeWidth={2} />}
              disabled={busy}
              onClick={handleAccept}
            >
              {accept.isPending ? 'Salvando…' : 'Aceitar'}
            </PillButton>
            <PillButton
              danger
              icon={<ThumbsDown size={13} strokeWidth={2} />}
              disabled={busy}
              onClick={() => setRejectOpen(true)}
            >
              Recusar
            </PillButton>
          </>
        )}

        {status === 'aceita' && convertedSubscriptionId === null && (
          <PillButton
            solid
            icon={<Sparkles size={13} strokeWidth={2} />}
            disabled={busy}
            onClick={openConvert}
          >
            {convert.isPending ? 'Convertendo…' : 'Converter em assinatura'}
          </PillButton>
        )}

        {showCopyLink && <CopyLinkButton copied={copied} onClick={handleCopyLink} />}

        {/* Duplicar / Renegociar — disponível em qualquer status não-terminal. */}
        <PillButton
          icon={<Files size={13} strokeWidth={2} />}
          disabled={busy}
          onClick={handleDuplicate}
        >
          {duplicate.isPending
            ? 'Duplicando…'
            : status === 'recusada' || status === 'expirada'
              ? 'Duplicar / Renegociar'
              : 'Duplicar'}
        </PillButton>
      </ActionBar>

      <RejectProposalDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        proposalNumber={proposalNumber}
        isPending={reject.isPending}
        onConfirm={handleReject}
      />

      <ConvertProposalDialog
        open={convertOpen}
        onOpenChange={(open) => {
          setConvertOpen(open);
          if (!open) setMissingFields(null);
        }}
        proposalNumber={proposalNumber}
        isPending={convert.isPending}
        missingFields={missingFields}
        onEditLead={leadId ? () => navigate(`/crm/leads/${leadId}`) : undefined}
        onConfirm={handleConvert}
      />
    </>
  );
}

function ActionBar({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

function CopyLinkButton({
  copied,
  onClick,
}: {
  copied: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <PillButton
      icon={copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={2} />}
      onClick={onClick}
    >
      {copied ? 'Copiado!' : 'Copiar link público'}
    </PillButton>
  );
}
